import { useEffect, useState } from 'react';
import type {
    FundusScreeningGetManualImportLogsApiResponse,
    FundusScreeningManualImportLogsSortByProperties,
} from '@common/interfaces/api/fundusScreeningApi';
import { getFundusScreeningManualImportLogsApi } from '@frontend/services/api/fundusScreeningApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { usePagination } from '@frontend/utils/hooks/usePagination';
import { useSorting } from '@frontend/utils/hooks/useSorting';

export function useFundusScreeningManualImportLogs() {
    const [isLoading, setIsLoading] = useState(true);
    const abortSignal = useAbortSignal();
    const [logs, setLogs] = useState<FundusScreeningGetManualImportLogsApiResponse['logs']>([]);

    const { handlePaginationChange, handleTotalItemsChange, paginationOptions } = usePagination({
        defaultPageSize: 10,
    });

    const { handleSort, sortingOptions } = useSorting<FundusScreeningManualImportLogsSortByProperties>({
        defaultSortByKey: 'importDate',
        defaultSortDirection: 'ASC',
    });

    useEffect(() => {
        const fetchLogs = async () => {
            const { logs: nextLogs, totalItems } = await getFundusScreeningManualImportLogsApi(
                paginationOptions,
                sortingOptions,
                abortSignal
            );

            setLogs(nextLogs);
            handleTotalItemsChange(totalItems);
            setIsLoading(false);
        };

        fetchLogs();
    }, [paginationOptions.page, paginationOptions.pageSize, sortingOptions]);

    return {
        isLoading,
        logs,
        pagination: {
            handleChange: handlePaginationChange,
            hasEnoughItemsToPaginate: paginationOptions.totalItems > 10,
            options: paginationOptions,
        },
        sorting: {
            by: sortingOptions.sortByKey,
            changeTo: handleSort,
            direction: sortingOptions.sortDirection,
        },
    };
}
